import { useTranslation } from 'react-i18next';
import { useDocumentParams } from 'app/router/params';
import { useNavigate } from '@tanstack/react-router';
import { TippyMenu } from 'app/components';
import { useState, useRef, useEffect } from 'react';

import { v4 as uuidv4 } from 'uuid';
import { useSaveTemplate, useDeleteTemplate, useCopyTemplate } from 'entities/template';
import { useUser } from 'app/state/contexts';
import { Modal } from 'app/components';
import authStore from 'shared/auth/authStore';
import { Level } from 'shared/ui/Level';
import { usePermissions } from 'app/api/permission-queries';

interface StructureNode {
  type?: string;
  className?: string;
  children?: StructureNode[];
}

export interface Template {
  superId?: string;
  name?: string;
  organization?: number | null;
  thumbnail?: string;
  structure?: StructureNode;
  [key: string]: unknown;
}

const useIsVisible = (rootMargin = '200px'): [React.RefObject<HTMLDivElement>, boolean] => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin]);

  return [ref, isVisible];
};

const StructureBlock = ({ node, depth = 0 }: { node?: StructureNode; depth?: number }) => {
  if (!node || !node.children) return null;

  const layouts = node.children.filter((c) => c.type === 'layout');
  const isRow = node.className?.includes('row') || node.className?.includes('horizontal');

  if (layouts.length === 0) {
    return <div style={{ flex: '1 1 0', minHeight: depth <= 1 ? 40 : 20, background: '#e9ecf1', borderRadius: 3 }} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: isRow ? 'row' : 'column', flex: '1 1 0', gap: isRow ? 5 : 3 }}>
      {layouts.map((child, i) => (
        <StructureBlock key={i} node={child} depth={depth + 1} />
      ))}
    </div>
  );
};

const StructureDiagram = ({ structure, aspectRatio = '210 / 297' }: { structure?: StructureNode; aspectRatio?: string }) => {
  const rows = structure?.children?.filter((c) => c.type === 'layout') || [];

  return (
    <div
      style={{
        height: '200px',
        aspectRatio,
        background: '#fff',
        border: '1px solid #e1e5eb',
        borderRadius: '6px 6px 0 0',
        padding: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        overflow: 'hidden',
      }}
    >
      {/* Header area */}
      <div style={{ height: 14, background: '#e9ecf1', borderRadius: 3, flexShrink: 0 }} />
      {/* Content area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {rows.length > 0 ? (
          rows.map((row, i) => <StructureBlock key={i} node={row} depth={0} />)
        ) : (
          <div style={{ flex: 1, background: '#e9ecf1', borderRadius: 3 }} />
        )}
      </div>
      {/* Footer area */}
      <div style={{ height: 10, background: '#e9ecf1', borderRadius: 3, flexShrink: 0 }} />
    </div>
  );
};

const DiagramPlaceholder = ({ aspectRatio = '210 / 297' }: { aspectRatio?: string }) => (
  <div
    style={{
      height: PREVIEW_HEIGHT,
      aspectRatio,
      background: '#f8f9fa',
      border: '1px solid #e1e5eb',
      borderRadius: '6px 6px 0 0',
    }}
  />
);

const PREVIEW_HEIGHT = 240;

const ThumbnailImage = ({ thumbnail, aspectRatio = '210 / 297' }: { thumbnail?: string; aspectRatio?: string }) => (
  <img
    src={`data:image/png;base64,${thumbnail}`}
    alt=""
    style={{
      height: PREVIEW_HEIGHT,
      aspectRatio,
      objectFit: 'fill',
      padding: 10,
      border: '1px solid #e1e5eb',
      borderRadius: '6px 6px 0 0',
    }}
  />
);

const PreviewImage = ({ organization, template, aspectRatio }: { organization?: number | null; template: Template; aspectRatio?: string }) => {
  const [ref, isVisible] = useIsVisible();

  const renderPreview = () => {
    if (!isVisible) return <DiagramPlaceholder aspectRatio={aspectRatio} />;
    if (template.thumbnail) return <ThumbnailImage thumbnail={template.thumbnail} aspectRatio={aspectRatio} />;
    return <StructureDiagram structure={template.structure} aspectRatio={aspectRatio} />;
  };

  return (
    <div className="position-relative" ref={ref} style={{ display: 'flex', justifyContent: 'center' }}>
      {renderPreview()}
      <div className="position-absolute bottom-0 end-0 me-2 mb-1">
        <Level organization={organization} />
      </div>
    </div>
  );
};

const RenameModal = ({ onClose, template }: { onClose: () => void; template: Template }) => {
  const { t } = useTranslation(['common', 'theme']);

  const [name, setName] = useState(template?.name);

  const { mutate: saveTemplate } = useSaveTemplate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleSave = () => {
    const newTemplate = {
      ...template,
      name,
      revisionId: uuidv4(),
    };

    saveTemplate(newTemplate);
    onClose();
  };

  return (
    <Modal show={true} onClose={onClose}>
      <label className="form-label" htmlFor={`input-name-${template?.superId}`}>
        {t('theme:structure.name')}
      </label>
      <input className="form-control" id={`input-name-${template?.superId}`} value={name} onChange={handleChange} />
      <div className="d-flex justify-content-end mt-5">
        <button type="button" className="btn" onClick={onClose}>
          {t('common:button.cancel')}
        </button>
        <button type="button" className="btn btn-primary" onClick={handleSave}>
          {t('common:button.save')}
        </button>
      </div>
    </Modal>
  );
};

interface StructurePreviewProps {
  template: Template;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  active?: boolean;
  onCopy?: (copiedTemplate: Template) => void;
  aspectRatio?: string;
  inModal?: boolean;
}

export const StructurePreview = ({ template, onClick, active, onCopy, aspectRatio, inModal = false }: StructurePreviewProps) => {
  const { t } = useTranslation('templates');

  const hasGlobalTemplatePermission = authStore.hasGlobalPermission('TEMPLATE_CREATE');

  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);
  const [templateToRename, setTemplateToRename] = useState<Template | null>(null);
  const { documentId } = useDocumentParams();
  const { documentPermissions } = usePermissions(documentId);
  const { mutate: saveTemplate } = useSaveTemplate();
  const { selectedOrganization } = useUser();
  const { mutate: deleteTemplate } = useDeleteTemplate();
  const { mutate: copyTemplate } = useCopyTemplate();

  const handleMakeGlobal = (template: Template) => {
    const newTemplate = {
      ...template,
      revisionId: uuidv4(),
      documentSuperId: null,
      organization: null,
    };

    saveTemplate(newTemplate);
  };

  const handleMakeOrganization = (template: Template) => {
    const newTemplate = {
      ...template,
      revisionId: uuidv4(),
      documentSuperId: null,
      organization: selectedOrganization,
    };
    saveTemplate(newTemplate);
  };

  const handleDelete = (template: Template) => {
    deleteTemplate(template as { superId: string; revisionId?: string });
  };

  const handleCopy = (template: Template) => {
    copyTemplate(
      { ...template, organization: selectedOrganization },
      {
        onSuccess: (copiedTemplate) => onCopy?.(copiedTemplate as Template),
      }
    );
  };

  const handleCloseRenameModal = () => {
    setTemplateToRename(null);
  };

  const buildMenu = (template: Template) => {
    const items = [];
    if ((template.organization && documentPermissions.includes('CODER')) || hasGlobalTemplatePermission) {
      items.push({
        text: t('dropdown.edit-name'),
        icon: 'fa-pen',
        onClick: () => setTemplateToRename(template),
      });
    }
    if ((template.organization && documentPermissions.includes('CODER')) || hasGlobalTemplatePermission) {
      items.push({
        text: t('dropdown.edit'),
        icon: 'fa-pen',
        onClick: () => navigate({ to: '/documents/$documentId/templates/$templateId', params: { documentId, templateId: template.superId ?? '' } }),
      });
    }

    if (template.organization && hasGlobalTemplatePermission) {
      items.push({
        text: t('dropdown.make-global-system'),
        icon: 'fa-cloud',
        onClick: () => handleMakeGlobal(template),
      });
    } else if (hasGlobalTemplatePermission) {
      items.push({
        text: t('dropdown.make-non-global'),
        icon: 'fa-cloud-slash',
        onClick: () => handleMakeOrganization(template),
      });
    }
    if (template.organization && (hasGlobalTemplatePermission || documentPermissions.includes('CODER'))) {
      items.push({
        text: t('dropdown.delete-template'),
        icon: 'fa-trash',
        onClick: () => handleDelete(template),
      });
    }
    if (documentPermissions.includes('CODER') || hasGlobalTemplatePermission) {
      items.push({
        text: t('dropdown.copy'),
        icon: 'fa-copy',
        onClick: () => handleCopy(template),
      });
    }
    return items;
  };
  return (
    <>
      <div onClick={onClick} ref={cardRef} className={`d-flex flex-column align-items-stretch border bg-white rounded ${active ? 'border-blue-800' : ''}`}>
        <button type="button" disabled={onClick ? false : true} className="border-0 p-0 bg-white rounded">
          <PreviewImage organization={template.organization} template={template} aspectRatio={aspectRatio} />
        </button>
        <div
          className={`px-1 text-black border-top text-left rounded-bottom d-flex justify-content-between align-items-center ${
            active ? 'border-blue-800 bg-blue-400' : 'bg-white'
          }`}
        >
          <div className="px-2 py-2">{template.name}</div>
          {(documentPermissions.includes('CODER') || documentPermissions.includes('DESIGN')) && buildMenu(template).length > 0 && (
            <TippyMenu
              menu={buildMenu(template)}
              placement="right-end"
              getReferenceClientRect={() => cardRef.current?.getBoundingClientRect() ?? new DOMRect()}
              appendTo={inModal ? 'parent' : () => document.body}
              popperOptions={{
                modifiers: [
                  { name: 'flip', enabled: false },
                  { name: 'preventOverflow', options: { mainAxis: false, altAxis: true } },
                ],
              }}
            />
          )}
        </div>
      </div>
      {templateToRename ? <RenameModal template={templateToRename} onClose={handleCloseRenameModal} /> : null}
    </>
  );
};
